// ============================================================================
// 진동 — 두 가지 백엔드를 한 API 뒤에 숨긴다.
//
//   웹 / Android      navigator.vibrate(ms)        길이(ms)를 그대로 준다
//   iOS 네이티브 앱    @capacitor/haptics           길이 개념이 없다. 세기 3단계뿐
//
// **왜 두 개인가:** iOS 의 WKWebView 에는 navigator.vibrate 가 아예 없다.
// 웹으로는 우회로가 없어서(지시서 §8 이 "iOS 는 불가"로 적어 둔 이유), 앱으로 감싼
// 뒤에야 CoreHaptics 에 닿을 수 있다. Android 는 웹 API 가 멀쩡히 동작하므로
// 플러그인을 거치지 않는다 — 브리지를 한 겹 더 태울 이유가 없다.
//
// **플랫폼이 아니라 기능으로 판정한다.** UA 로 iOS 를 찍으면 iOS 사파리(웹)와
// iOS 앱을 구분하지 못하고, 나중에 다른 래퍼를 쓰면 또 깨진다.
// 여기서는 "Capacitor 네이티브 플랫폼인가 + Haptics 플러그인이 붙어 있는가"만 본다.
//
// ── ms → 세기 변환 ──────────────────────────────────────────────────────────
// 이 게임의 진동은 전부 `pulse(ms)` 로 되어 있다 (params.ts 의 P.haptic.*).
// iOS 는 길이를 못 받으므로 ms 를 Light/Medium/Heavy 셋 중 하나로 접는다.
// 문턱은 P.haptic.iosLightMax / iosMediumMax 에 있다 — 코드에 숫자를 박지 않는다.
// **이 대응은 아직 실기기에서 맞춰 보지 않았다.** 값은 출발점일 뿐이고
// 폰에서 만져 본 뒤 조정해야 한다.
//
// ── 연속 진동의 문제 ────────────────────────────────────────────────────────
// `cutContinuous` 는 자르는 동안 초당 22~70회 펄스를 쏜다. 웹에서는 모터를 직접
// 때리므로 문제가 없지만, iOS 는 매번 JS→네이티브 브리지를 타는 **비동기 호출**이다.
// 그 속도로 부르면 큐가 밀리고 손을 뗀 뒤에도 진동이 남는다.
// 그래서 iOS 경로에만 최소 간격(P.haptic.iosMinIntervalMs)을 둬서 솎아낸다.
// 솎인 호출은 rejected 로 세지 않는다 — 실패가 아니라 의도된 것이다.
// ============================================================================

import { P } from './params';

/** UA 로 브라우저 종류를 추정. 인앱 브라우저(카톡/라인/페북 등)는 진동 권한이 없는 경우가 많다. */
function detectBrowser(ua: string): string {
  const t: [RegExp, string][] = [
    [/KAKAOTALK/i, '카카오톡 인앱'],
    [/NAVER\(inapp/i, '네이버 인앱'],
    [/Line\//i, '라인 인앱'],
    [/FBAN|FBAV|FB_IAB/i, '페이스북 인앱'],
    [/Instagram/i, '인스타그램 인앱'],
    [/DaumApps/i, '다음 인앱'],
    [/SamsungBrowser/i, '삼성 인터넷'],
    [/EdgA?\//i, 'Edge'],
    [/FxiOS|Firefox/i, 'Firefox'],
    [/CriOS/i, 'Chrome (iOS)'],
    [/Chrome\//i, 'Chrome'],
    [/Safari\//i, 'Safari'],
  ];
  for (const [re, name] of t) if (re.test(ua)) return name;
  return '알 수 없음';
}

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const isWebView = /;\s*wv\)/i.test(ua); // Android WebView (인앱 브라우저 대부분)

// ── 네이티브 백엔드 감지 ─────────────────────────────────────────────────────
// Capacitor 가 없는 순수 웹에서도 이 파일이 그대로 돌아야 하므로(개발 서버·배포 URL),
// 전역을 조심스럽게 들여다본다. import 는 하지 않는다 — 웹 번들에 플러그인을
// 끌어들이지 않기 위해서다. @capacitor/haptics 는 네이티브에서 window.Capacitor 에
// 붙으므로 여기서 꺼내 쓸 수 있다.
type ImpactStyle = 'HEAVY' | 'MEDIUM' | 'LIGHT';
interface NativeHaptics {
  impact(opts: { style: ImpactStyle }): Promise<void>;
}
interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
  Plugins?: { Haptics?: NativeHaptics };
}

const cap: CapacitorGlobal | undefined =
  typeof window !== 'undefined' ? (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor : undefined;

const isNative = !!cap?.isNativePlatform?.();
const nativePlatform = isNative ? (cap?.getPlatform?.() ?? '') : '';
const nativeHaptics: NativeHaptics | null =
  isNative && cap?.isPluginAvailable?.('Haptics') ? (cap?.Plugins?.Haptics ?? null) : null;

// 웹 진동 가능 여부는 예전 그대로.
const webVibrate = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

// **Android 는 웹 API 를 그대로 쓴다.** 네이티브 앱이어도 navigator.vibrate 가 동작하고,
// 길이를 ms 로 직접 줄 수 있어서 플러그인(세기 3단계)보다 표현력이 높다.
// iOS 에서만 플러그인으로 간다.
const useNative = !!nativeHaptics && !webVibrate;

export const haptics = {
  enabled: true,
  supported: webVibrate || useNative,
  secure: typeof window !== 'undefined' && window.isSecureContext === true,
  browser: detectBrowser(ua),
  webView: isWebView,
  ua,

  /** 어느 백엔드로 나가는지. 진단에 쓴다. */
  backend: useNative ? `native:${nativePlatform || '?'}` : webVibrate ? 'web' : 'none',

  // 진단
  calls: 0,
  rejected: 0,
  throttled: 0, // iOS 최소 간격에 걸려 솎인 횟수 (실패가 아니다)
  lastMs: 0,
  lastResult: null as boolean | null,
  interacted: false, // 사용자 제스처가 한 번이라도 있었는지

  /** iOS 솎아내기용. 마지막으로 네이티브에 실제로 보낸 시각. */
  _lastNativeAt: 0,

  /** 실제 손맛용 펄스. 배열이면 [진동, 정지, 진동, ...] 패턴. */
  pulse(v: number | number[]): void {
    if (!this.enabled) return;
    if (Array.isArray(v)) {
      const pat = v.map((x) => Math.max(0, Math.round(x)));
      if (!pat.some((x) => x > 0)) return;
      this.fire(pat);
    } else {
      if (v <= 0) return;
      this.fire(Math.round(v));
    }
  },

  /** 자가진단용. enabled 와 무관하게 무조건 쏜다. */
  test(v: number | number[]): boolean {
    return this.fire(v);
  },

  fire(v: number | number[]): boolean {
    if (!this.supported) {
      this.lastResult = false;
      return false;
    }
    this.lastMs = Array.isArray(v) ? v.reduce((a, b) => a + b, 0) : v;

    if (useNative) return this._fireNative(this.lastMs);

    this.calls++;
    let ok = false;
    try {
      ok = navigator.vibrate(v) !== false;
    } catch {
      ok = false;
    }
    this.lastResult = ok;
    if (!ok) this.rejected++;
    return ok;
  },

  /**
   * iOS 경로. 길이를 세기로 접고, 너무 잦은 호출은 솎아낸다.
   * **동기처럼 쓰되 기다리지 않는다** — 240Hz sim 이 이 호출을 기다리면 안 된다.
   */
  _fireNative(ms: number): boolean {
    const hp = P.haptic;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this._lastNativeAt < hp.iosMinIntervalMs) {
      this.throttled++;
      return true; // 거부가 아니다. 의도적으로 건너뛴 것.
    }
    this._lastNativeAt = now;
    this.calls++;

    const style: ImpactStyle = ms <= hp.iosLightMax ? 'LIGHT' : ms <= hp.iosMediumMax ? 'MEDIUM' : 'HEAVY';
    try {
      // 반환 Promise 를 붙잡지 않는다. 실패해도 다음 프레임을 막지 않는 게 더 중요하다.
      void nativeHaptics!.impact({ style }).catch(() => {
        this.rejected++;
        this.lastResult = false;
      });
      this.lastResult = true;
      return true;
    } catch {
      this.rejected++;
      this.lastResult = false;
      return false;
    }
  },

  stop(): void {
    // 네이티브 impact 는 한 번 때리고 끝이라 멈출 대상이 없다.
    // 대신 솎아내기 시계를 초기화해서, 손을 뗐다가 다시 그으면 첫 펄스가 안 먹는 일을 막는다.
    if (useNative) {
      this._lastNativeAt = 0;
      return;
    }
    // 아직 한 번도 안 쏘았으면 멈출 것도 없다. 로드 직후(사용자 터치 전) 호출하면
    // Chrome 이 "user hasn't tapped" 인터벤션 경고를 콘솔에 남긴다.
    if (webVibrate && this.calls > 0) {
      try {
        navigator.vibrate(0);
      } catch {
        /* ignore */
      }
    }
  },

  /** 패널/진단에 띄울 한 줄 요약 */
  status(): string {
    if (!this.supported) return `미지원 (${this.browser}) — 이 브라우저에는 진동 API 가 없다`;
    if (useNative) {
      return `네이티브 · ${nativePlatform} · 세기 3단계 · 호출 ${this.calls}(거부 ${this.rejected} 솎임 ${this.throttled})`;
    }
    const r = this.lastResult === null ? '호출 전' : this.lastResult ? 'true(수락)' : 'false(거부)';
    return `지원 · ${this.browser}${this.webView ? ' · 인앱' : ''} · ${this.secure ? 'HTTPS' : 'HTTP'} · 반환 ${r} · 호출 ${this.calls}(거부 ${this.rejected})`;
  },

  /** 안 되는 이유로 가장 유력한 것 */
  verdict(): string {
    if (!this.supported) {
      return /iPhone|iPad|iPod/i.test(this.ua)
        ? 'iOS 웹에는 진동 API 가 없다. 앱(Capacitor)으로 열면 동작한다 — tools/build-ipa.sh.'
        : '이 브라우저에 진동 API 가 없다. Chrome 으로 열어볼 것.';
    }
    if (useNative) {
      if (this.rejected > 0) return `네이티브 호출 ${this.rejected}건이 실패했다. Xcode 콘솔 로그를 볼 것.`;
      if (this.calls === 0) return '아직 한 번도 안 쏘았다.';
      return '네이티브로 나가고 있다. 안 느껴지면 기기 설정 > 사운드 및 햅틱 > 시스템 햅틱을 확인.';
    }
    if (!this.interacted) return '아직 화면을 안 만졌다. 한 번 터치한 뒤 다시 시도.';
    if (this.lastResult === false) {
      if (!this.secure) return '호출이 거부됐다. HTTP 접속이 원인일 수 있다 — HTTPS 로 열어볼 것.';
      return '브라우저가 호출을 거부했다. 사용자 제스처 또는 브라우저 정책 문제.';
    }
    if (this.lastResult === true) {
      return '브라우저는 수락했다. 안 느껴지면 기기 쪽이다 — 설정 > 소리 및 진동 > 진동 세기 / 터치 피드백, 절전 모드, 방해 금지를 확인.';
    }
    return '아래 테스트 버튼을 눌러 볼 것.';
  },
};
